// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
import { useCallback } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SuperadminService } from "@/services/superadmin";
import { StatsCards } from "@/components/superadmin/overview/StatsCards";
import { RecentActivityFeed } from "@/components/superadmin/overview/RecentActivityFeed";
import { SystemStatusWidget } from "@/components/superadmin/overview/SystemStatusWidget";
import { QuickActions } from "@/components/superadmin/overview/QuickActions";

type TimeRange = "24h" | "7d" | "30d";

export const Route = createFileRoute("/superadmin/")({
  component: OverviewPage,
  validateSearch: (search: Record<string, unknown>) => ({
    timeRange: (["24h", "7d", "30d"].includes(search.timeRange as string)
      ? search.timeRange
      : "30d") as TimeRange,
  }),
});

function OverviewPage() {
  const { t } = useTranslation("superadmin");
  const { timeRange } = Route.useSearch();
  const navigate = useNavigate();

  const statsQuery = useQuery({
    queryKey: ["superadmin", "platformStats", timeRange],
    queryFn: () => SuperadminService.getPlatformStats(timeRange),
  });

  const activityQuery = useQuery({
    queryKey: ["superadmin", "recentActivity"],
    queryFn: () => SuperadminService.getRecentActivity(10),
  });

  const healthQuery = useQuery({
    queryKey: ["superadmin", "systemHealth"],
    queryFn: () => SuperadminService.getSystemHealth(),
    refetchInterval: 60000,
  });

  const handleTimeRangeChange = useCallback(
    (value: string) => {
      navigate({
        to: "/superadmin",
        search: { timeRange: value } as any,
        replace: true,
      });
    },
    [navigate],
  );

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t("overview.title")}</h1>
          <p className="text-muted-foreground">{t("overview.description")}</p>
        </div>
        <Select value={timeRange} onValueChange={handleTimeRangeChange}>
          <SelectTrigger className="w-[180px]" aria-label={t("overview.timeRange.label")}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="24h">{t("overview.timeRange.24h")}</SelectItem>
            <SelectItem value="7d">{t("overview.timeRange.7d")}</SelectItem>
            <SelectItem value="30d">{t("overview.timeRange.30d")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats cards row */}
      <StatsCards stats={statsQuery.data} isLoading={statsQuery.isLoading} />

      {/* Two-column layout: activity + sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* On mobile, status + quick actions come first */}
        <div className="flex flex-col gap-6 lg:hidden">
          <SystemStatusWidget health={healthQuery.data} isLoading={healthQuery.isLoading} />
          <QuickActions />
        </div>

        {/* Activity feed - 2/3 width on desktop */}
        <div className="lg:col-span-2">
          <RecentActivityFeed
            activities={activityQuery.data}
            isLoading={activityQuery.isLoading}
          />
        </div>

        {/* Right sidebar - 1/3 width on desktop, hidden on mobile (shown above) */}
        <div className="hidden lg:flex lg:flex-col gap-6">
          <SystemStatusWidget health={healthQuery.data} isLoading={healthQuery.isLoading} />
          <QuickActions />
        </div>
      </div>
    </div>
  );
}
